import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setQueueApiEndpoint, setUser } from "../../app/appSlice";
import { useWorkOrderStore } from "../store/zustandWorkOrderStore";
import { USER_IDENTITY } from "../common/constants/filterParamConstants";
import ApplicationUserAPI from "./api/applicationUserApi";
import {
  logAmplitudeEventWithProps,
  createAmplitudeEventProperties,
} from "components/common/amplitude/amplitude";
import packageJson from "../../../package.json";
import { clearSchedulingLookupCaches } from "../common/Utils/cacheUtil";

interface UrlAuthParams {
  username?: string;
  password?: string;
  companyId?: string;
  companyName?: string;
  databaseId?: string;
  authToken?: string;
  queueApiEndpoint?: string;
}

interface DecodedJWT {
  sub?: string; // username
  jti?: string;
  cpid?: string; // company id
  cpname?: string; //company name
  dbid?: string; // database id
  ctx?: string; // context token
  ctxh?: string;
  sid?: string;
  svcqueueurl?: string; // queue API
  nbf?: number;
  exp?: number;
  iat?: number;
  iss?: string;
  aud?: string;
  use?: string;
}

export default function EmbeddedAutoLogin() {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasRedirected, setHasRedirected] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setFilters } = useWorkOrderStore();
  const authStartTime = useRef<number>(Date.now());

  // Function to decode JWT token
  const decodeJWT = (token: string): DecodedJWT | null => {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map(function (c) {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join(""),
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error("Error decoding JWT:", error);
      return null;
    }
  };

  // Function to check if JWT is expired
  const isJWTExpired = (jwt: DecodedJWT): boolean => {
    if (!jwt.exp) return true;
    const currentTime = Math.floor(Date.now() / 1000);
    return currentTime > jwt.exp;
  };

  // Detect if we're running in an embedded context
  const isEmbedded = () => {
    try {
      // Check if we're in an iframe or embedded web view
      return (
        window.self !== window.top ||
        window.location.href.includes("embedded") ||
        navigator.userAgent.includes("Electron") ||
        navigator.userAgent.includes("WebView")
      );
    } catch (e) {
      // If we can't access window.top, we're likely embedded
      return true;
    }
  };

  // Listen for messages from desktop app
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === "REDIRECT_TO_Login") {
        // console.log(
        //   "EmbeddedAutoLogin: Received redirect message from desktop app",
        // );
        window.location.href = event.data.url || "/";
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // Helper function for robust navigation to Login page
  const redirectToLogin = () => {
    if (hasRedirected) {
      // console.log(
      //   "EmbeddedAutoLogin: Already redirected, preventing infinite loop",
      // );
      return;
    }

    setHasRedirected(true);
    const embedded = isEmbedded();
    // console.log(
    //   "EmbeddedAutoLogin: Redirecting to Login, embedded context:",
    //   embedded,
    // );

    // Clear any authentication data to prevent dashboard access
    localStorage.removeItem("authToken");
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("user");
    localStorage.removeItem(USER_IDENTITY);
    // console.log("EmbeddedAutoLogin: Cleared authentication data");

    if (embedded) {
      // For embedded contexts, use direct navigation immediately
      // console.log(
      //   "EmbeddedAutoLogin: Using direct navigation for embedded context",
      // );

      // IMMEDIATE redirect for embedded contexts
      //console.log("EmbeddedAutoLogin: Immediate redirect to Login page");
      window.location.href = "/";

      // Try multiple navigation methods for embedded contexts
      try {
        window.location.href = "/";
      } catch (e) {
        // console.warn(
        //   "window.location.href failed, trying window.location.replace",
        // );
        window.location.replace("/");
      }

      // Additional immediate fallback for stubborn embedded contexts
      setTimeout(() => {
        if (window.location.pathname !== "/") {
          // console.warn(
          //   "Direct navigation failed, trying window.location.replace",
          // );
          try {
            window.location.replace("/");
          } catch (e) {
            //console.warn("window.location.replace failed, trying postMessage");
            // Try to communicate with parent/desktop app
            if (window.parent && window.parent !== window) {
              window.parent.postMessage(
                { type: "REDIRECT_TO_Login", url: "/" },
                "*",
              );
            }
          }
        }
      }, 100);

      // Set up periodic check for navigation success
      const checkInterval = setInterval(() => {
        if (window.location.pathname === "/") {
          //console.log("EmbeddedAutoLogin: Navigation to Login successful");
          clearInterval(checkInterval);
        } else {
          // console.warn(
          //   "EmbeddedAutoLogin: Navigation still pending, trying again",
          // );
          try {
            window.location.href = "/";
          } catch (e) {
            //console.warn("Periodic navigation attempt failed");
          }
        }
      }, 500);

      // Clear interval after 10 seconds to prevent infinite attempts
      setTimeout(() => clearInterval(checkInterval), 10000);

      return;
    }

    try {
      // Try React Router navigation first for browser contexts
      //console.log("EmbeddedAutoLogin: Using React Router navigation");
      navigate("/", { replace: true });
    } catch (navError) {
      //console.warn("React Router navigation failed, using fallback:", navError);
      // Fallback: force page reload to Login
      window.location.href = "/";
    }

    // Additional fallback for embedded contexts
    setTimeout(() => {
      // Check if we're still on the same page (navigation might have failed)
      if (window.location.pathname !== "/") {
        //console.warn("Navigation to Login failed, forcing page reload");
        window.location.href = "/";
      }
    }, 1000);
  };

  // Parse URL query parameters
  const parseUrlParams = (): UrlAuthParams => {
    const urlParams = new URLSearchParams(window.location.search);
    const queueApiEndpoint = urlParams.get("queueApiEndpoint");

    return {
      username: urlParams.get("username") || undefined,
      password: urlParams.get("password") || undefined,
      companyId: urlParams.get("companyId") || undefined,
      databaseId: urlParams.get("databaseId") || undefined,
      authToken: urlParams.get("authToken") || undefined,
      queueApiEndpoint: queueApiEndpoint
        ? decodeURIComponent(queueApiEndpoint)
        : undefined,
    };
  };

  useEffect(() => {
    const params = parseUrlParams();

    // Check if we have the minimum required parameters
    if (params.authToken) {
      // Check for token in localStorage first (injected by desktop app)
      const localStorageToken = localStorage.getItem("authToken");

      if (params.authToken || localStorageToken) {
        // If we have a token (from URL or localStorage), use it directly
        const tokenToUse = params.authToken || localStorageToken;

        // Decode JWT to extract user information
        const decodedJWT = decodeJWT(tokenToUse!);
        if (decodedJWT) {
          //console.log("EmbeddedAutoLogin: JWT decoded successfully");
          // console.log("EmbeddedAutoLogin: Decoded JWT properties:", {
          //   sub: decodedJWT.sub,
          //   cpid: decodedJWT.cpid,
          //   dbid: decodedJWT.dbid,
          //   svcqueueurl: decodedJWT.svcqueueurl,
          //   exp: decodedJWT.exp,
          // });

          // Check if token is expired
          if (isJWTExpired(decodedJWT)) {
            setError("Authentication token has expired. Please log in again.");
            setIsLoading(false);
            setTimeout(() => {
              redirectToLogin();
            }, 3000);
            return;
          }

          // Set up localStorage with JWT data
          localStorage.setItem("authToken", tokenToUse!);
          localStorage.setItem(
            "username",
            decodedJWT.sub || params.username || "",
          );
          localStorage.setItem(
            "companyId",
            decodedJWT.cpid || params.companyId || "",
          );
          localStorage.setItem("companyName", decodedJWT.cpname || "");
          localStorage.setItem(
            "databaseId",
            decodedJWT.dbid || params.databaseId || "",
          );

          // Set queue API endpoint from JWT if available
          if (decodedJWT.svcqueueurl) {
            // Normalize the API endpoint URL (remove trailing slash if present)
            let normalizedEndpoint = decodedJWT.svcqueueurl;
            if (normalizedEndpoint.endsWith("/")) {
              normalizedEndpoint = normalizedEndpoint.slice(0, -1);
            }

            localStorage.setItem("queueAPI", normalizedEndpoint);
            dispatch(setQueueApiEndpoint(normalizedEndpoint));
            // console.log(
            //   "EmbeddedAutoLogin: Set queue API from JWT:",
            //   normalizedEndpoint,
            // );
          } else if (params.queueApiEndpoint) {
            // Normalize the API endpoint URL (remove trailing slash if present)
            let normalizedEndpoint = params.queueApiEndpoint;
            if (normalizedEndpoint.endsWith("/")) {
              normalizedEndpoint = normalizedEndpoint.slice(0, -1);
            }

            localStorage.setItem("queueAPI", normalizedEndpoint);
            dispatch(setQueueApiEndpoint(normalizedEndpoint));
            // console.log(
            //   "EmbeddedAutoLogin: Set queue API from URL params:",
            //   normalizedEndpoint,
            // );
          }

          // Use JWT data to populate missing parameters
          const enhancedParams = {
            ...params,
            authToken: tokenToUse,
            username: decodedJWT.sub || params.username,
            companyId: decodedJWT.cpid || params.companyId,
            companyName: decodedJWT.cpname || undefined,
            databaseId: decodedJWT.dbid || params.databaseId,
            queueApiEndpoint: decodedJWT.svcqueueurl || params.queueApiEndpoint,
          };

          // console.log(
          //   "EmbeddedAutoLogin: Using enhanced parameters from JWT:",
          //   {
          //     username: enhancedParams.username,
          //     companyId: enhancedParams.companyId,
          //     databaseId: enhancedParams.databaseId,
          //     queueApiEndpoint: enhancedParams.queueApiEndpoint,
          //     tokenExpiry: decodedJWT.exp
          //       ? new Date(decodedJWT.exp * 1000).toLocaleString()
          //       : "unknown",
          //   },
          // );

          performTokenBasedAuth(enhancedParams);
        } else {
          // Fallback to original parameters if JWT decoding fails
          const authParams = { ...params, authToken: tokenToUse };
          performTokenBasedAuth(authParams);
        }
      } else {
        setError(
          "Missing required parameters. Need either authToken (in URL or localStorage) or password.",
        );
        setIsLoading(false);
      }
    } else {
      setError(
        "Missing required parameters: username, companyId, and databaseId are required.",
      );
      setIsLoading(false);
    }
  }, []);

  // Handle error state and redirect logic
  useEffect(() => {
    if (error && !hasRedirected) {
      // console.log(
      //   "EmbeddedAutoLogin: Error state detected, forcing redirect to Login",
      // );
      setTimeout(() => {
        redirectToLogin();
      }, 100);
    }
  }, [error, hasRedirected]);

  async function performTokenBasedAuth(params: UrlAuthParams) {
    try {
      //debugger;
      setIsLoading(true);
      setError(null);

      // console.log("EmbeddedAutoLogin: Using token-based authentication");
      // console.log("EmbeddedAutoLogin: Parameters:", {
      //   username: params.username,
      //   companyId: params.companyId,
      //   databaseId: params.databaseId,
      //   authToken: params.authToken ? "***" : "undefined",
      //   queueApiEndpoint: params.queueApiEndpoint,
      // });

      // Authentication token should already be set in localStorage from JWT decoding
      // Just verify it's there
      const storedAuthToken = localStorage.getItem("authToken");
      const storedUsername = localStorage.getItem("username");
      //const storedQueueAPI = localStorage.getItem("queueAPI");

      // console.log("EmbeddedAutoLogin: Stored values from localStorage:", {
      //   username: storedUsername,
      //   queueAPI: storedQueueAPI,
      //   authToken: storedAuthToken ? "***" : "undefined",
      // });

      if (!storedAuthToken) {
        throw new Error("No authentication token found in localStorage");
      }

      if (!storedUsername) {
        throw new Error("No username found in localStorage");
      }

      clearSchedulingLookupCaches(storedUsername);

      // Small delay to ensure localStorage is properly set
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Get user information using the token
      // console.log(
      //   "EmbeddedAutoLogin: Calling getUserByUserName for:",
      //   storedUsername,
      // );
      const userInfo =
        await ApplicationUserAPI.getUserByUserName(storedUsername);

      if (userInfo) {
        //console.log("EmbeddedAutoLogin: User info retrieved successfully");
        await setupUserAndCache(userInfo, params);
      } else {
        const errorMsg = "Failed to fetch user information";
        console.error("EmbeddedAutoLogin:", errorMsg);
        setError(errorMsg);
        // Redirect to Login page after showing error
        const timeout = isEmbedded() ? 1000 : 3000; // Faster redirect for embedded contexts
        setTimeout(() => {
          redirectToLogin();
        }, timeout);
      }
    } catch (err: any) {
      console.error("EmbeddedAutoLogin: Token-based auth error:", err);
      console.error("EmbeddedAutoLogin: Error details:", {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        url: err.config?.url,
        baseURL: err.config?.baseURL,
      });

      let errorMsg = "Failed to authenticate with token. Please try again.";

      if (err.response?.status === 404) {
        errorMsg =
          "API endpoint not found. Please check the queueApiEndpoint parameter.";
      } else if (err.code === "ERR_NETWORK") {
        errorMsg =
          "Network error. Please check your connection and API endpoint.";
      } else if (err.response?.status === 401) {
        errorMsg =
          "Invalid authentication token. Please check your credentials.";
      }

      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }

  async function setupUserAndCache(userInfo: any, params: UrlAuthParams) {
    try {
      if (!userInfo || !userInfo.identity) {
        console.error(
          "EmbeddedAutoLogin: Invalid user info, redirecting to Login",
        );
        redirectToLogin();
        return;
      }

      userInfo.userNameAndCompany = `${userInfo.userName}@${localStorage.getItem("companyName")}`;
      userInfo.company = localStorage.getItem("companyName");

      await dispatch(setUser(userInfo));
      localStorage.setItem(USER_IDENTITY, userInfo.identity?.toString() || "");
      localStorage.setItem("user", JSON.stringify(userInfo));

      setFilters("userIdentity", userInfo.identity);

      localStorage.setItem("companyId", params.companyId!);
      localStorage.setItem("databaseId", params.databaseId!);
      localStorage.setItem("selectedCompany", params.companyId!);
      localStorage.setItem("selectedDatabase", params.databaseId!);
      localStorage.setItem("isAuthenticated", "true");
      localStorage.setItem("authenticationTimestamp", new Date().toISOString());

      // console.log("EmbeddedAutoLogin: All cache variables set successfully");
      // console.log("EmbeddedAutoLogin: Cache variables set:", {
      //   authToken: "***",
      //   username: params.username,
      //   companyId: params.companyId,
      //   databaseId: params.databaseId,
      //   queueAPI: localStorage.getItem("queueAPI"),
      //   userIdentity: userInfo.identity,
      //   isAuthenticated: "true",
      // });

      const elapsedMs = Date.now() - authStartTime.current;
      const userAndCompany = `${userInfo.userName}@${localStorage.getItem("companyName")}`;
      //debugger;
      logAmplitudeEventWithProps(
        userAndCompany,
        {
          company: localStorage.getItem("companyName"),
          user: userInfo.userName || "",
        },
        "SQ-EmbeddedLogin",
        createAmplitudeEventProperties(
          "SQ-EmbeddedLogin",
          "SQ-EmbeddedAutoLogin.tsx",
          packageJson.version,
          elapsedMs.toString(),
        ),
      );

      const newUrl = window.location.pathname;
      window.history.replaceState({}, "", newUrl);

      //console.log("EmbeddedAutoLogin: Redirecting to QueueBoard");
      navigate("/QueueBoard", { replace: true });
    } catch (error) {
      // console.error(
      //   "EmbeddedAutoLogin: Error setting up user and cache:",
      //   error,
      // );
      setError("Failed to set up user session. Please try again.");
    }
  }

  // This logic should not run on every render - it's handled in useEffect

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-700 mb-2">
            Setting up your session...
          </h2>
          <p className="text-gray-500">
            Please wait while we configure your application.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <div className="text-red-500 mb-4">
            <svg
              className="w-16 h-16 mx-auto"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Setup Failed
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors mr-3"
          >
            Try Again
          </button>
          <button
            onClick={() => navigate("/")}
            className="bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
          >
            Go to Login
          </button>
          {isEmbedded() && (
            <button
              onClick={() => {
                // console.log(
                //   "EmbeddedAutoLogin: Force redirect to Login for embedded context",
                // );
                window.location.href = "/";
              }}
              className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-md transition-colors ml-2"
            >
              Force Redirect
            </button>
          )}
        </div>
      </div>
    );
  }

  return null;
}
